/**
 * A Markdown table, in a reply and in a doc: Streamdown's `table` replaced,
 * so both read the same. One light frame, never a height cap. A table that
 * fits is as wide as its column and lays out as before. One that doesn't
 * (a phone, mostly) keeps readable columns and scrolls sideways: each cell
 * wraps at 18rem (a label column at 10rem), the first column stays put,
 * and the far edge fades while there's more to see.
 */
import { cn } from "@crew/ui/lib/utils";
import { useEffect, useRef, useState, type ComponentProps, type RefObject } from "react";

type Edges = { scrolled: boolean; more: boolean };

/** Whether the scroller has moved from its start, and whether more is past its end. */
function useScrollEdges(scroller: RefObject<HTMLElement | null>): Edges {
  const [edges, setEdges] = useState<Edges>({ scrolled: false, more: false });
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const read = () => {
      const scrolled = el.scrollLeft > 1;
      const more = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((e) => (e.scrolled === scrolled && e.more === more ? e : { scrolled, more }));
    };
    read();
    el.addEventListener("scroll", read, { passive: true });
    // A streaming reply's table grows, and a turned phone widens the frame.
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    resize?.observe(el);
    if (el.firstElementChild) resize?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", read);
      resize?.disconnect();
    };
  }, [scroller]);
  return edges;
}

const CELLS = cn(
  // Wide as its content, never narrower than the frame. A cell's cap only
  // binds once the table overflows, so a table that fits is unchanged.
  "w-max min-w-full divide-y divide-border",
  // A long word or URL breaks too, so it can't push a cell past its cap.
  "[&_td]:max-w-72 [&_th]:max-w-72 [&_th]:whitespace-normal [&_td]:wrap-anywhere [&_th]:wrap-anywhere",
  // The first column is sticky, so it needs a background of its own: the
  // header's tint is mixed onto the page rather than see-through.
  "[&_thead]:bg-transparent [&_th]:bg-[color-mix(in_oklab,var(--color-secondary)_50%,var(--color-background))]",
  "[&_td:first-child]:sticky [&_td:first-child]:left-0 [&_td:first-child]:z-1 [&_td:first-child]:bg-background",
  "[&_th:first-child]:sticky [&_th:first-child]:left-0 [&_th:first-child]:z-1",
  "[&_td:first-child]:max-w-40 [&_th:first-child]:max-w-40",
);

/** Scrolled: a hairline where the columns slide under the first. */
const SCROLLED =
  "[&_td:first-child]:shadow-[1px_0_0] [&_td:first-child]:shadow-ink/8 [&_th:first-child]:shadow-[1px_0_0] [&_th:first-child]:shadow-ink/8";

/** More to the right: the far edge fades out. */
const MORE = "mask-r-from-[calc(100%-2.5rem)]";

export function MarkdownTable({
  children,
  className,
  node: _node,
  ...props
}: ComponentProps<"table"> & { node?: unknown }) {
  const scroller = useRef<HTMLDivElement>(null);
  const { scrolled, more } = useScrollEdges(scroller);
  const overflows = scrolled || more;
  return (
    <div
      className="my-4 overflow-hidden rounded-md border border-border bg-background"
      data-streamdown="table-wrapper"
    >
      <div
        ref={scroller}
        className={cn("overflow-x-auto overscroll-x-contain", more && MORE)}
        data-overflows={overflows || undefined}
        // A table that scrolls can be scrolled from the keyboard too.
        tabIndex={overflows ? 0 : undefined}
      >
        <table
          className={cn(CELLS, scrolled && SCROLLED, className)}
          data-streamdown="table"
          {...props}
        >
          {children}
        </table>
      </div>
    </div>
  );
}
